/**
 * Patients API - extends the generated OpenAPI client
 *
 * Provides cache management for patient clinical info (demographics, medications, diagnoses)
 * with automatic invalidation when patient data is updated.
 */

import { enhancedApi } from '@store/generated/patients';

/**
 * Feature-specific tag types for patient cache management
 */
const PATIENT_TAG_TYPES = ['PatientClinicalInfo', 'PatientCommunicationsPreferences'] as const;

/**
 * Patients API - extends the generated endpoints with cache tags
 */
export const patientsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PATIENT_TAG_TYPES,
  endpoints: {
    /**
     * GET /v2/provider/patients/{patientId}/clinical-info
     * Fetches patient demographics + medications + diagnoses + last session
     */
    getV2ProviderPatientsByPatientIdClinicalInfo: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}
     * Updates patient demographics fields
     * Invalidates clinical info cache to trigger refetch
     */
    patchV2ProviderPatientsByPatientId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/communications-preferences
     * Fetches patient communication preferences including preferred language
     */
    getV2ProviderPatientsByPatientIdCommunicationsPreferences: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
      ],
    },

    /**
     * PUT /v2/provider/patients/{patientId}/communications-preferences/language
     * Updates patient preferred language (syncs to Stripe)
     */
    putV2ProviderPatientsByPatientIdCommunicationsPreferencesLanguage: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
      ],
    },

    /**
     * PUT /v2/provider/patients/{patientId}/communications-preferences/{preferenceKey}
     * Updates a specific communication preference (email, sms, phone_call)
     */
    putV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKey: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
      ],
    },
  },
});

// =============================================================================
// User-Friendly Hook Exports
// =============================================================================

// Queries
export const usePatientClinicalInfoQuery =
  patientsApi.useGetV2ProviderPatientsByPatientIdClinicalInfoQuery;
export const useLazyPatientClinicalInfoQuery =
  patientsApi.useLazyGetV2ProviderPatientsByPatientIdClinicalInfoQuery;
export const useCommunicationsPreferencesQuery =
  patientsApi.useGetV2ProviderPatientsByPatientIdCommunicationsPreferencesQuery;

// Mutations
export const useUpdatePatientMutation = patientsApi.usePatchV2ProviderPatientsByPatientIdMutation;
export const useUpdateLanguagePreferenceMutation =
  patientsApi.usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesLanguageMutation;
export const useUpdateCommunicationPreferenceMutation =
  patientsApi.usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKeyMutation;

// =============================================================================
// Type Re-exports for Convenience
// =============================================================================

export type {
  PatientClinicalInfo2 as PatientClinicalInfo,
  PatientClinicalInfoResponse,
  UpdatePatientRequest,
  // Communication Preferences types
  CommunicationsPreferencesResponse,
  ContactMethodPreference,
  UpdateLanguagePreferenceRequest,
  UpdateCommunicationPreferenceRequest,
} from '@store/generated/patients';

